// [목적] collection 도메인 zod 스키마 — 컬렉션 CRUD + 아이템 멤버십 BE/FE 공유
// [이유] folio-social-and-profile Spec WC-DTO-COLLECTION-03 — FE CollectionPage mock fixture 대체.
//         kind='official'/'auto' 는 클라이언트 입력 금지(서버 강제) 정책을 스키마 레벨에서 표현
// [사이드 이팩트] apps/api(collection-social) + apps/web(CollectionPage) 양쪽 회귀 검증 필요
// [수정 시 고려사항] slug 자동 생성 정책(Q-9) — 클라이언트가 slug 를 입력할 수 없으므로 createInput 에서 제외.
//                   patchCollectionInputSchema 에서 slug/kind 필드 변경 시 zod 거부(strict) — 400 반환(Q-19)

import { z } from 'zod';

// ─── Enums ────────────────────────────────────────────────────────────────────

export const collectionKindEnum = z.enum(['official', 'editor', 'auto']);
export const collectionVisibilityEnum = z.enum(['public', 'link', 'private']);

// ─── Collection ───────────────────────────────────────────────────────────────

/**
 * DB row 직렬화 형태 — collections 테이블 컬럼과 1:1 매핑.
 * itemCount — 서비스 레이어에서 COUNT 집계 후 주입.
 */
export const collectionSchema = z.object({
  id: z.string().uuid(),
  curatorId: z.string().uuid().nullable(),
  slug: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  coverUrl: z.string().url().nullable(),
  kind: collectionKindEnum,
  visibility: collectionVisibilityEnum,
  itemCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});

/**
 * GET /collections/:id 상세 응답 — collection + itemsPreview(top N=20 폴리오).
 * itemsPreview 내 폴리오는 folioSchema 대신 경량 snapshot(id/title/coverImageUrl만) 사용.
 */
export const collectionItemPreviewSchema = z.object({
  portfolioId: z.string().uuid(),
  ord: z.number().int(),
  addedAt: z.string(),
  // 폴리오 경량 snapshot
  title: z.string(),
  coverImageUrl: z.string().nullable(),
  slug: z.string(),
});

export const collectionDetailSchema = collectionSchema.extend({
  itemsPreview: z.array(collectionItemPreviewSchema),
});

// ─── Input schemas ────────────────────────────────────────────────────────────

/**
 * POST /collections 요청 body.
 * slug — 서버 자동 생성(Q-9), 클라이언트 미입력.
 * kind — 서버에서 'editor' 강제, 클라이언트 미입력.
 */
export const createCollectionInputSchema = z.object({
  title: z.string().min(1).max(80),
  summary: z.string().max(400).optional().nullable(),
  coverUrl: z.string().url().max(2000).optional().nullable(),
  visibility: collectionVisibilityEnum.default('public'),
});

/**
 * PATCH /collections/:id 요청 body.
 * slug/kind 변경 금지 — strict() 로 추가 필드 400 반환(Q-19).
 */
export const patchCollectionInputSchema = z
  .object({
    title: z.string().min(1).max(80).optional(),
    summary: z.string().max(400).optional().nullable(),
    coverUrl: z.string().url().max(2000).optional().nullable(),
    visibility: collectionVisibilityEnum.optional(),
  })
  .strict();

/**
 * POST /collections/:id/items 요청 body.
 */
export const addCollectionItemInputSchema = z.object({
  portfolioId: z.string().uuid(),
});

/**
 * GET /collections 쿼리 파라미터.
 * [목적] kind 필터를 collectionKindEnum(official/editor/auto) 전체로 확대 — fe-be-gap-tier1 T9(ADR-TR-5)
 * [이유] 기존 z.literal('editor')는 'official' 컬렉션 조회를 스키마 레벨에서 차단했음.
 *        FE Explore 공식 컬렉션 섹션·Sidebar official 4건 조회를 위해 공개 kind 전체 허용.
 * [사이드 이팩트] GET /collections?kind=official 이 이제 통과됨. service/repository 는 이미
 *                 opts.kind 필터를 전달하므로 추가 변경 불필요.
 * [수정 시 고려사항] createCollectionInput 에서 kind 를 받지 않으므로 클라이언트가 official 생성은 불가.
 *                   POST /collections 의 editor 강제 정책(createCollectionInputSchema)은 불변.
 */
export const collectionListQuerySchema = z.object({
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  curatorId: z.string().uuid().optional(),
  kind: collectionKindEnum.optional(),
});

export type Collection = z.infer<typeof collectionSchema>;
export type CollectionDetail = z.infer<typeof collectionDetailSchema>;
export type CollectionItemPreview = z.infer<typeof collectionItemPreviewSchema>;
export type CreateCollectionInput = z.infer<typeof createCollectionInputSchema>;
export type PatchCollectionInput = z.infer<typeof patchCollectionInputSchema>;
export type AddCollectionItemInput = z.infer<typeof addCollectionItemInputSchema>;
export type CollectionListQuery = z.infer<typeof collectionListQuerySchema>;
